'use client';

import { useMemo, useState } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { InformationCircleIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Badge } from '../../../components/badge';
import { Checkbox } from '../../../components/checkbox';
import { MetaDefault } from '../../../components/typography';
import { TableGrid, type TableGridRow } from '../../table-grid/TableGrid';
import { EvidenceDrawer } from './EvidenceDrawer';
import { humanizeSnakeCase } from './humanize-snake-case';
import { scopeBadgeStyle } from './scope-constants';
import { EntityContainer, ScopeEmptyState, SkeletonList } from './scope-shared';
import type { DomainsSectionProps, ScopeEvidence } from './scope-types';
import { ScopeMenu } from './ScopeMenu';

export function DomainsSection({
  domains,
  totalDomains,
  isLoading,
  selectedIds,
  scopeFilter,
  isDisabled,
  onSelectionChange,
  onScopeChange,
}: DomainsSectionProps) {
  const t = useTranslations('designSystem.networkSettings.externalScope');
  const [drawerState, setDrawerState] = useState<{
    title: string;
    evidences: ScopeEvidence[];
  } | null>(null);
  const rows = useMemo<TableGridRow[]>(() => {
    return domains
      .filter((domain) => scopeFilter === 'all' || domain.scope === scopeFilter)
      .map((domain) => ({
        id: domain.id,
        value: [
          domain.fqdn,
          domain.scope,
          domain.sourceType,
          domain.isTopLevel ? t('domainTopLevel') : t('domainSubdomain'),
        ]
          .filter(Boolean)
          .join(' '),
        cells: [
          <HStack key="domain" gap={2} alignItems="center" minW={0}>
            <Checkbox
              checked={selectedIds.includes(domain.id)}
              onCheckedChange={(details) => {
                const isChecked =
                  typeof details === 'boolean' ? details : details.checked;

                onSelectionChange(
                  isChecked
                    ? [...selectedIds, domain.id]
                    : selectedIds.filter((id) => id !== domain.id),
                );
              }}
              size="sm"
              disabled={isDisabled}
            />
            <Box truncate minW={0}>
              {domain.fqdn}
            </Box>
          </HStack>,
          humanizeSnakeCase(domain.sourceType),
          domain.evidences.length > 0 ? (
            <Box
              key="evidence"
              onClick={() =>
                setDrawerState({
                  title: domain.fqdn,
                  evidences: domain.evidences,
                })
              }
              cursor="pointer"
            >
              <Badge variant="outline" size="sm" colorPalette="gray">
                {domain.evidences.length}
              </Badge>
            </Box>
          ) : null,
          <HStack
            key="scope"
            gap={1}
            alignItems="center"
            justifyContent="space-between"
            minW={0}
          >
            <Badge
              variant="fill"
              size="sm"
              {...scopeBadgeStyle({ scope: domain.scope })}
            >
              {humanizeSnakeCase(domain.scope)}
            </Badge>
            <ScopeMenu
              isDisabled={isDisabled}
              onScopeChange={(scope) => onScopeChange({ id: domain.id, scope })}
            />
          </HStack>,
        ],
      }));
  }, [
    domains,
    scopeFilter,
    selectedIds,
    isDisabled,
    onSelectionChange,
    onScopeChange,
    t,
  ]);
  const isTruncated = domains.length < totalDomains;

  if (isLoading) {
    return (
      <EntityContainer>
        <SkeletonList count={4} height="48px" />
      </EntityContainer>
    );
  }

  if (domains.length === 0) {
    return (
      <EntityContainer>
        <ScopeEmptyState />
      </EntityContainer>
    );
  }

  return (
    <VStack gap={3} alignItems="stretch">
      {isTruncated && (
        <HStack
          gap={2}
          px={3}
          py={2}
          bg="bg.warning.subtle"
          borderRadius="md"
          borderWidth="1px"
          borderColor="border.warning.subtle"
          alignItems="center"
        >
          <InformationCircleIcon
            size={13}
            style={{ flexShrink: 0 }}
            color="var(--colors-fg-warning-default, currentColor)"
          />
          <MetaDefault color="fg.warning.default">
            {t('domainTreeTruncated', {
              shown: domains.length,
              total: totalDomains,
            })}
          </MetaDefault>
        </HStack>
      )}
      <EntityContainer>
        <TableGrid
          columns={[
            { id: 'domain', title: t('fqdn'), width: 'minmax(0, 1fr)' },
            { id: 'source', title: t('source'), width: 'minmax(0, 1fr)' },
            { id: 'evidence', title: t('evidence'), width: '72px' },
            { id: 'scope', title: t('scopeColumn'), width: '124px' },
          ]}
          rows={rows}
          placeholder={t('searchDomains')}
          tableMaxHeight="50vh"
          emptyState={<ScopeEmptyState />}
        />
      </EntityContainer>
      <EvidenceDrawer
        isOpen={drawerState !== null}
        onClose={() => setDrawerState(null)}
        title={drawerState?.title ?? ''}
        evidences={drawerState?.evidences ?? []}
      />
    </VStack>
  );
}
